import type { Weekday } from "./scheduleTypes";

export type { Weekday };

/**
 * Story refresh scheduling types.
 *
 * The story-canvas "Schedule refresh" modal follows a recurring-event pattern
 * driven by a single `Repeat` preset (Figma nodes 5401:217407 / 217951 /
 * 217658 and 5407:218108) — the rest of the form swaps per preset. That is a
 * different shape from the connector `ScheduleRefreshConfig`, which is an
 * always-visible `every` + `unit` stepper, so it gets its own config type
 * rather than being bolted onto `BaseScheduleConfig`.
 *
 * The `Weekday` primitive is shared with DeliverStory / SchedulePicker —
 * schedule editors must agree on weekday identifiers.
 */

/** The four options in the `Repeat` dropdown. Drives the whole conditional form. */
export type RepeatPreset = "none" | "daily" | "weekly" | "monthly";

/** `Ends` radio group. Only meaningful for the recurring presets. */
export type EndsMode = "never" | "on" | "after";

/** Monthly `Repeat on` options, derived from the selected `Starts on` date. */
export type MonthlyRepeatOn =
  | "day-of-month"
  | "nth-weekday"
  | "last-day"
  | "last-weekday";

export interface StoryRefreshConfig {
  /** Which preset is selected — decides which of the fields below are live. */
  repeat: RepeatPreset;
  /** Interval count. Weekly / monthly only ("Repeat every N Week/Month"). */
  every: number;
  /** Selected weekdays. Weekly only. */
  repeatOn: Weekday[];
  /** Day of month 1–31, derived from `startsOn`. Monthly only. */
  repeatDom: number | null;
  /** Which monthly recurrence option the user selected. Monthly only. */
  monthlyRepeatOn: MonthlyRepeatOn | null;
  /** ISO `yyyy-mm-dd`. The one-off run date — `none` preset only. */
  scheduleOn: string;
  /** ISO `yyyy-mm-dd`. First occurrence — weekly and monthly. */
  startsOn: string;
  /** Hour of day 0–23 when the refresh runs. Every preset. */
  refreshHour: number | null;
  /** IANA timezone identifier sent with every schedule. */
  timezone: string;
  /** End condition. Weekly / monthly only. */
  endsMode: EndsMode;
  /** ISO `yyyy-mm-dd`. Required when `endsMode === "on"`. */
  endsOn: string;
  /** Occurrence count. Required when `endsMode === "after"`. */
  endsAfter: number | null;
}

export type StoryRefreshField =
  | "every"
  | "repeatOn"
  | "repeatDom"
  | "monthlyRepeatOn"
  | "scheduleOn"
  | "startsOn"
  | "refreshHour"
  | "timezone"
  | "endsOn"
  | "endsAfter";

export type StoryRefreshErrors = Partial<Record<StoryRefreshField, string>>;

/** Presets that recur, and therefore render interval and end-condition fields. */
export const isRecurring = (repeat: RepeatPreset): boolean =>
  repeat === "weekly" || repeat === "monthly";

/**
 * Validate only the fields the active preset actually renders.
 *
 * This is the whole point of keeping validation preset-aware: a stale
 * `repeatOn` left over from Weekly must not block a Monthly save, and a
 * hidden field can never produce an error the user cannot see or fix.
 * `setRepeatPreset` in `useStoryScheduleRefresh` clears those fields too —
 * belt and braces, because the config is what gets submitted.
 */
export const validateStoryRefresh = (
  cfg: StoryRefreshConfig,
): StoryRefreshErrors => {
  const errors: StoryRefreshErrors = {};

  // Schedule at — the one field every preset renders.
  if (cfg.refreshHour === null) {
    errors.refreshHour = "Select a time";
  }

  if (cfg.repeat === "none") {
    if (!cfg.scheduleOn) errors.scheduleOn = "Pick a date";
    return errors;
  }

  if (cfg.repeat === "daily") {
    return errors;
  }

  if (!cfg.timezone) {
    errors.timezone = "Select a timezone";
  }

  // Weekly + monthly share the interval and end-condition fields.
  if (!Number.isFinite(cfg.every) || cfg.every < 1) {
    errors.every = "Must be at least 1";
  }

  if (cfg.repeat === "weekly") {
    if (cfg.repeatOn.length === 0) {
      errors.repeatOn = "Select at least one day";
    }
    if (!cfg.startsOn) {
      errors.startsOn = "Pick a start date";
    }
  } else {
    if (!cfg.startsOn) {
      errors.startsOn = "Pick a start date";
    }
    if (!cfg.monthlyRepeatOn) {
      errors.monthlyRepeatOn = "Select a repeat option";
    }
  }

  if (cfg.endsMode === "on" && !cfg.endsOn) {
    errors.endsOn = "Pick an end date";
  }

  // Weekly and monthly both render the "After N occurrences" row, so both
  // validate the count — otherwise a null would be posted as a silent 1.
  if (
    cfg.endsMode === "after" &&
    (cfg.endsAfter === null || !Number.isFinite(cfg.endsAfter) || cfg.endsAfter < 1)
  ) {
    errors.endsAfter = "Must be at least 1";
  }

  return errors;
};

export const isStoryRefreshValid = (cfg: StoryRefreshConfig): boolean =>
  Object.keys(validateStoryRefresh(cfg)).length === 0;
